import React, { createContext, useContext, useState } from 'react'

export type VisaoFaseamento = 'ano' | 'trimestre' | 'mes' | 'semanas'

interface GeralFilterContextType {
  year: number
  compareYear: number
  month: number
  compareMonth: number
  endMonth: number
  distributorId: string | null
  distributorIds: (string | number)[]
  startDate: Date | null
  endDate: Date | null
  visao: VisaoFaseamento
  semana: string
  semanas: string[]
  setSemana: (s: string) => void
  setSemanas: (s: string[]) => void
  toggleSemana: (s: string) => void
  selectAllSemanas: () => void
  clearSemanas: () => void
  trimestre: string
  trimestres: string[]
  setTrimestre: (t: string) => void
  setTrimestres: (t: string[]) => void
  toggleTrimestre: (q: string) => void
  selectAllTrimestres: () => void
  clearTrimestres: () => void
  mesesAno: number[]
  setMesesAno: (meses: number[]) => void
  toggleMesAno: (mes: number) => void
  selectAllMesesAno: () => void
  clearMesesAno: () => void
  setYear: (y: number) => void
  setCompareYear: (y: number) => void
  setMonth: (m: number) => void
  setCompareMonth: (m: number) => void
  setEndMonth: (m: number) => void
  setDistributor: (id: string | null) => void
  setDistributorIds: (ids: (string | number)[]) => void
  toggleDistributor: (id: string | number) => void
  selectAllDistributors: (ids: (string | number)[]) => void
  clearDistributors: () => void
  setStartDate: (date: Date | null) => void
  setEndDate: (date: Date | null) => void
  setVisao: (v: VisaoFaseamento) => void
  operacao: string
  setOperacao: (op: string) => void
  gestor: string
  setGestor: (g: string) => void
  marca: string
  setMarca: (m: string) => void
  categoria: string
  setCategoria: (c: string) => void
  uf: string
  setUf: (u: string) => void
}

const GeralFilterContext = createContext<GeralFilterContextType | undefined>(undefined)

export const GeralFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const now = new Date()

  const [year, setYearState] = useState<number>(now.getFullYear())
  const [compareYear, setCompareYear] = useState<number>(now.getFullYear() - 1)
  const [month, setMonthState] = useState<number>(now.getMonth())
  const [compareMonth, setCompareMonth] = useState<number>(now.getMonth())
  const [endMonth, setEndMonthState] = useState<number>(now.getMonth())
  const [distributorId, setDistributor] = useState<string | null>(null)
  const [distributorIds, setDistributorIds] = useState<(string | number)[]>([])
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [visao, setVisao] = useState<VisaoFaseamento>('semanas')
  const [semanas, setSemanasState] = useState<string[]>([])
  const [trimestres, setTrimestresState] = useState<string[]>([])
  const [mesesAno, setMesesAno] = useState<number[]>([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11])
  const [operacao, setOperacao] = useState<string>('')
  const [gestor, setGestor] = useState<string>('')
  const [marca, setMarca] = useState<string>('')
  const [categoria, setCategoria] = useState<string>('')
  const [uf, setUf] = useState<string>('')

  const semana = semanas.length === 0 ? 'todas' : semanas.join(',')
  const trimestre = trimestres.length === 0 ? 'todos' : trimestres.join(',')

  const setSemana = (s: string) => {
    if (!s || s === 'todas') {
      setSemanasState([])
    } else {
      setSemanasState(s.split(',').map((x) => x.trim()).filter(Boolean))
    }
  }

  const setSemanas = (list: string[]) => {
    setSemanasState(list)
  }

  const toggleSemana = (s: string) => {
    if (s === 'todas') {
      setSemanasState([])
      return
    }
    setSemanasState((prev) => {
      if (prev.includes(s)) {
        return prev.filter((item) => item !== s)
      } else {
        const next = [...prev, s]
        if (next.length === 4) return [] // Todas selecionadas = sem filtro específico
        return next.sort()
      }
    })
  }

  const selectAllSemanas = () => {
    setSemanasState([])
  }

  const clearSemanas = () => {
    setSemanasState([])
  }

  const setTrimestre = (t: string) => {
    if (!t || t === 'todos') {
      setTrimestresState([])
    } else {
      setTrimestresState(t.split(',').map((x) => x.trim()).filter(Boolean))
    }
  }

  const setTrimestres = (list: string[]) => {
    setTrimestresState(list)
  }

  const toggleTrimestre = (q: string) => {
    if (q === 'todos') {
      setTrimestresState([])
      return
    }
    setTrimestresState((prev) => {
      if (prev.includes(q)) {
        return prev.filter((item) => item !== q)
      } else {
        const next = [...prev, q]
        if (next.length === 4) return []
        return next.sort()
      }
    })
  }

  const selectAllTrimestres = () => {
    setTrimestresState([])
  }

  const clearTrimestres = () => {
    setTrimestresState([])
  }

  const toggleMesAno = (m: number) => {
    setMesesAno((prev) => {
      if (prev.includes(m)) {
        return prev.filter((item) => item !== m).sort((a, b) => a - b)
      } else {
        return [...prev, m].sort((a, b) => a - b)
      }
    })
  }

  const selectAllMesesAno = () => {
    setMesesAno([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11])
  }

  const clearMesesAno = () => {
    setMesesAno([])
  }

  const setYear = (y: number) => {
    setYearState(y)
    setCompareYear((prev) => (prev >= y ? y - 1 : prev))
  }

  const setMonth = (m: number) => {
    setMonthState(m)
    if (m !== -1 && endMonth < m) {
      setEndMonthState(m)
    }
  }

  const setEndMonth = (m: number) => {
    setEndMonthState(m)
    if (m !== -1 && month !== -1 && m < month) {
      setMonthState(m)
    }
  }

  const toggleDistributor = (id: string | number) => {
    setDistributor(null)
    setDistributorIds((prev) => {
      const exists = prev.some((item) => String(item) === String(id))
      if (exists) {
        return prev.filter((item) => String(item) !== String(id))
      } else {
        return [...prev, id]
      }
    })
  }

  const selectAllDistributors = (ids: (string | number)[]) => {
    setDistributor(null)
    setDistributorIds(ids)
  }

  const clearDistributors = () => {
    setDistributor(null)
    setDistributorIds([])
  }

  return (
    <GeralFilterContext.Provider
      value={{
        year,
        compareYear,
        month,
        compareMonth,
        endMonth,
        distributorId,
        distributorIds,
        startDate,
        endDate,
        visao,
        semana,
        semanas,
        setSemana,
        setSemanas,
        toggleSemana,
        selectAllSemanas,
        clearSemanas,
        trimestre,
        trimestres,
        setTrimestre,
        setTrimestres,
        toggleTrimestre,
        selectAllTrimestres,
        clearTrimestres,
        mesesAno,
        setMesesAno,
        toggleMesAno,
        selectAllMesesAno,
        clearMesesAno,
        setYear,
        setCompareYear,
        setMonth,
        setCompareMonth,
        setEndMonth,
        setDistributor,
        setDistributorIds,
        toggleDistributor,
        selectAllDistributors,
        clearDistributors,
        setStartDate,
        setEndDate,
        setVisao,
        operacao,
        setOperacao,
        gestor,
        setGestor,
        marca,
        setMarca,
        categoria,
        setCategoria,
        uf,
        setUf,
      }}
    >
      {children}
    </GeralFilterContext.Provider>
  )
}

export const useGeralFilter = () => {
  const context = useContext(GeralFilterContext)
  if (!context) throw new Error('useGeralFilter deve ser usado dentro de GeralFilterProvider')
  return context
}
